import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IChangeArgs, IWizardStepRendererProps, StackPanel, Typography, useWizardStep } from '@agilepoint/wat-react-ux';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { IMobileApplicationDetails } from 'modules/create-new-app/queries';
import { MobileAppHelpLink, StyledStackPanel } from 'common';
import { WizardNavigationActions } from '../footers';
import { PlatformTypesForm } from '../forms';

interface IPlatformProps extends IWizardStepRendererProps {
    defaultValues?: IPlatformTypesFormFields
    onSavePlatformDetails: (formData: IPlatformTypesFormFields) => Promise<{
        UpdateMobileAppResult: IMobileApplicationDetails;
    }>
}

export enum PlatformTypes {
    Android = 'Android',
    IOS = 'iOS'
}

export interface IPlatformTypesFormFields {
    platformTypes: string[];
    [PlatformTypes.Android]: {
        buildTypes: string;
        packageName: string;
        certificate: IChangeArgs;
        keyAlias: string;
        keyPassword: string;
        storePassword: string;
    };
    [PlatformTypes.IOS]: {
        buildTypes: string;
        certificate?: IChangeArgs;
        password?: string;
        provisioningProfile?: IChangeArgs;
    };
}

export const Platform = React.memo((props: IPlatformProps) => {
    const { stepId, defaultValues, onSavePlatformDetails } = props;
    const { saveData, data } = useWizardStep(stepId);
    const translate = useTranslate();

    const form = useForm<IPlatformTypesFormFields>({
        mode: 'onChange',
        defaultValues: defaultValues ?? {
            Android: {
                buildTypes: 'MDM'
            },
            iOS: {
                buildTypes: 'MDM'
            }
        }
    });

    const { trigger, getValues, reset } = form;

    const onNext = React.useCallback(async () => {
        if (!await trigger()) {
            return false;
        }
        const formData = getValues() as IPlatformTypesFormFields;

        return onSavePlatformDetails(formData).then((result) => {
            saveData({ updateMobileAppRsponse: result.UpdateMobileAppResult, formData });
            return true;
        }).catch(() => false);
    }, [trigger, getValues, onSavePlatformDetails, saveData]);

    React.useEffect(() => {
        data && reset(data.formData);
    }, [reset, data]);

    return (
        <FormProvider {...form}>
            <MobileAppHelpLink id="platform" />
            <StyledStackPanel margin='30px 0px 0px 0px' alignment="center" width="100%" height='calc(100% - 50px)' orientation='vertical' gap="40">
                <StackPanel gap="10" orientation='vertical' alignment="center" width='100%'>
                    <Typography.H2 title={translate('modules.createNewApp.steps.platform.platformHeader')}><Localize translate='modules.createNewApp.steps.platform.platformHeader' /></Typography.H2>
                    <Typography.Medium title={translate('modules.createNewApp.steps.platform.platformSubtitle')}><Localize translate='modules.createNewApp.steps.platform.platformSubtitle' /></Typography.Medium>
                </StackPanel>
                <PlatformTypesForm />
            </StyledStackPanel>
            <WizardNavigationActions
                stepId={stepId}
                hook={onNext} />
        </FormProvider>
    );
});
